import React, { Component } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { Link } from "react-router-dom";
import "react-big-calendar/lib/css/react-big-calendar.css";
import AddService from "../components/AddService.tsx";
import ServiceDetail from "../components/ServiceDetail.tsx";
import LeaveModal from "../components/LeaveModal.tsx";
import ServiceApiService from "../services/service.service";
import UserService from "../services/user.service";
import TechnicianService from "../services/technician.service";
import type { CalendarDisplaySettings, ServiceItem, LeaveItem } from "./models";

const localizer = momentLocalizer(moment);
const BigCalendar: React.ElementType = Calendar as unknown as React.ElementType;

type ServiceCalendarState = {
  servicesData: ServiceItem[];
  leavesData: LeaveItem[];
  showAddService: boolean;
  showServiceDetail: boolean;
  showLeaveModal: boolean;
  selectedLeave: LeaveItem | null;
  showLeaves: boolean;
  selectedSlotStart: Date | null;
  currentService: ServiceItem | null;
  date: Date;
  companyShortName: string;
} & CalendarDisplaySettings;

const buildCalendarTime = (hour: number, isEnd = false) => {
  return new Date(1972, 0, 1, hour, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
};

type ServiceCalendarStatus =
  | "scheduled"
  | "confirmed"
  | "paid"
  | "done"
  | "client_not_show_up"
  | "canceled_rob"
  | "canceled_client"
  | "canceled_overtime";

const eventStyleGetter = (event: any) => {
  if (event.isLeave) {
    return {
      style: {
        backgroundColor: "#7209b7",
        color: "white",
      },
    };
  }
  const colors: Record<ServiceCalendarStatus, string> = {
    scheduled: "yellow",
    confirmed: "red",
    paid: "#87C54D",
    done: "#10A689",
    client_not_show_up: "black",
    canceled_rob: "#6c757d",
    canceled_client: "#6c757d",
    canceled_overtime: "#6c757d",
  };
  const status = event.status as ServiceCalendarStatus;
  return {
    style: {
      backgroundColor: colors[status] || "#2D9BF0",
      color: status === "scheduled" ? "black" : "white",
    },
  };
};

export default class ServiceCalendar extends Component<Record<string, never>, ServiceCalendarState> {
  interval?: ReturnType<typeof setInterval>;

  constructor(props: Record<string, never>) {
    super(props);
    this.state = {
      servicesData: [] as ServiceItem[],
      leavesData: [] as LeaveItem[],
      showAddService: false,
      showServiceDetail: false,
      showLeaveModal: false,
      selectedLeave: null,
      showLeaves: localStorage.getItem("showLeaves") !== "false",
      selectedSlotStart: null,
      currentService: null as ServiceItem | null,
      date: new Date(),
      calendarStartHour: 8,
      calendarEndHour: 18,
      companyShortName: "RDS",
    };
  }

  handleShowLeavesChangedFromEvent = () => {
    this.setState({
      showLeaves: localStorage.getItem("showLeaves") !== "false"
    }, () => {
      this.getServicesAndLeaves();
    });
  };

  handleLeavesUpdatedFromEvent = () => {
    this.getServicesAndLeaves();
  };

  componentDidMount() {
    this.loadCalendarSettings();
    this.getServicesAndLeaves();
    window.addEventListener("showLeavesChanged", this.handleShowLeavesChangedFromEvent);
    window.addEventListener("leavesUpdated", this.handleLeavesUpdatedFromEvent);
    this.interval = setInterval(() => this.getServicesAndLeaves(), 15000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener("showLeavesChanged", this.handleShowLeavesChangedFromEvent);
    window.removeEventListener("leavesUpdated", this.handleLeavesUpdatedFromEvent);
  }

  loadCalendarSettings = async () => {
    try {
      const result = await UserService.getAccountSettings();
      this.setState({
        calendarStartHour: result.data.data?.calendarStartHour ?? 8,
        calendarEndHour: result.data.data?.calendarEndHour ?? 18,
        companyShortName: result.data.data?.companyShortName ?? "RDS",
      });
    } catch (error) {
      console.log(error);
    }
  };

  getServicesAndLeaves = async (props?: Date) => {
    try {
      const date = props || this.state.date;
      const firstDay = moment(
        new Date(date.getFullYear(), date.getMonth(), -10)
      )
        .toISOString()
        .replace("Z", "");
      const lastDay = moment(
        new Date(date.getFullYear(), date.getMonth() + 1, 10)
      )
        .toISOString()
        .replace("Z", "");

      const servicesPromise = ServiceApiService.getServices(firstDay, lastDay);
      const leavesPromise = this.state.showLeaves
        ? TechnicianService.getLeaves(firstDay, lastDay)
        : Promise.resolve({ data: { data: [] } });

      const [servicesResult, leavesResult] = await Promise.all([
        servicesPromise,
        leavesPromise,
      ]);

      const newServices = servicesResult.data.data || [];
      const newLeaves = (leavesResult.data.data || []).map((leave: any) => ({
        ...leave,
        isLeave: true,
      }));

      if (
        JSON.stringify(this.state.servicesData) !== JSON.stringify(newServices) ||
        JSON.stringify(this.state.leavesData) !== JSON.stringify(newLeaves)
      ) {
        this.setState({ servicesData: newServices, leavesData: newLeaves });
      }
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error
      ) {
        const apiError = error as { response?: { data?: unknown } };
        console.log(apiError.response?.data);
      }
    }
  };

  handleShowLeavesChange = (showLeaves: boolean) => {
    localStorage.setItem("showLeaves", String(showLeaves));
    this.setState({ showLeaves }, () => {
      this.getServicesAndLeaves();
    });
  };

  handleSelectSlot = ({ start }: { start: Date }) => {
    this.setState({
      showAddService: true,
      selectedSlotStart: this.normalizeStartDate(start),
    });
  };

  openAddService = (start?: Date | null) => {
    this.setState({
      showAddService: true,
      selectedSlotStart: this.normalizeStartDate(start),
    });
  };

  normalizeStartDate = (start?: Date | null) => {
    if (!start) {
      return null;
    }

    if (
      start.getHours() === 0 &&
      start.getMinutes() === 0 &&
      start.getSeconds() === 0 &&
      start.getMilliseconds() === 0
    ) {
      const { calendarStartHour } = this.state;
      const normalizedStart = new Date(start);
      normalizedStart.setHours(calendarStartHour, 0, 0, 0);
      return normalizedStart;
    }

    return start;
  };

  handleSelectEvent = (event: any) => {
    if (event.isLeave) {
      this.setState({ showLeaveModal: true, selectedLeave: event });
    } else {
      this.setState({ showServiceDetail: true, currentService: event });
    }
  };

  onServiceChanged = () => {
    this.getServicesAndLeaves();
  };

  viewChanged = (date: Date) => {
    this.setState({ date });
    this.getServicesAndLeaves(date);
  };

  formatTitle = (event: any) => {
    if (event.isLeave) {
      return `Vacation: ${event.technicianName || event.technician_name || "Technician"}`;
    }
    const hour = moment(event.startDateTime).format("h a");
    if (event.client) {
      return `${hour}: ${event.client.clientName} (${event.durationHours}h)`;
    }
    return `${hour}: Service (${event.durationHours}h)`;
  };

  render() {
    const {
      servicesData,
      leavesData,
      showAddService,
      showServiceDetail,
      showLeaveModal,
      selectedLeave,
      showLeaves,
      selectedSlotStart,
      currentService,
      date,
      calendarStartHour,
      calendarEndHour,
      companyShortName,
    } = this.state;

    const calendarMin = buildCalendarTime(calendarStartHour);
    const calendarMax = buildCalendarTime(calendarEndHour, true);

    const combinedEvents = showLeaves
      ? [...servicesData, ...leavesData]
      : servicesData;

    return (
      <>
        <div className="container">
          <h2 className="mb-3">{date.toDateString()}</h2>
          <div className="d-flex justify-content-end align-items-center mb-2 gap-2">
            <Link
              to="/service/list"
              className="btn btn-outline-secondary btn-sm"
              aria-label="Search"
              title="Search"
            >
              <span aria-hidden="true">🔍</span>
            </Link>
          </div>
          <BigCalendar
            views={["month", "week", "agenda"]}
            selectable
            popup
            longPressThreshold={10}
            localizer={localizer}
            date={date}
            defaultView="month"
            events={combinedEvents}
            eventPropGetter={eventStyleGetter}
            style={{ height: "85vh" }}
            min={calendarMin}
            max={calendarMax}
            scrollToTime={calendarMin}
            onSelectEvent={this.handleSelectEvent}
            onSelectSlot={this.handleSelectSlot}
            onNavigate={this.viewChanged}
            startAccessor={(event: any) => new Date(event.startDateTime || event.start_date_time)}
            endAccessor={(event: any) => new Date(event.endDateTime || event.end_date_time)}
            titleAccessor={this.formatTitle}
          />
        </div>
        <Button
          variant="primary"
          className="d-md-none position-fixed"
          style={{ right: 16, bottom: 16, zIndex: 1050 }}
          onClick={() => this.openAddService(new Date())}
        >
          New Service
        </Button>
        <AddService
          show={showAddService}
          onHide={() => this.setState({ showAddService: false })}
          defaultStartDate={selectedSlotStart}
          onServiceCreated={this.onServiceChanged}
        />
        <ServiceDetail
          show={showServiceDetail}
          onHide={() => this.setState({ showServiceDetail: false })}
          service={currentService}
          companyShortName={companyShortName}
          onServiceChange={this.onServiceChanged}
        />
        <LeaveModal
          show={showLeaveModal}
          onHide={() => this.setState({ showLeaveModal: false, selectedLeave: null })}
          leave={selectedLeave}
          defaultStartDate={selectedSlotStart}
          onLeaveChanged={this.onServiceChanged}
          filterType="service"
        />
      </>
    );
  }
}
